import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Eye, EyeOff, Loader2, Lock, Mail, ArrowRight } from "lucide-react";
import axiosInstance from "@/lib/axios";
import { login } from "@/config/endpoints";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAppDispatch } from "@/stores/hook";
// Bundled (inlined) so the login logo can never 404 on the host's static path.
import xaleLogo from "@/assets/Logo.png";
import { setTokenAndUser } from "@/stores/slices/authSlice";
import { resetRoutesForNewSession } from "@/hooks/api/useRouterData";
import { takeReturnTo } from "@/features/auth/routes/loginReturn";
import { signupResumeUrl } from "@/features/auth/routes/signupResume";

const LoginSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type LoginFormData = z.infer<typeof LoginSchema>;

const MARKETING_BASE =
  import.meta.env.VITE_LOGIN_URL?.replace(/\/$/, "") || "https://xale.in";

export default function EmployeeLogin() {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(LoginSchema),
  });
  const dispatch = useAppDispatch();

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const res = await axiosInstance.post(login, data);
      const token = res.data.accessToken;
      const user = res.data.data.user;
      // Sign-up not finished (no plan yet) → back to that step, not the CRM.
      const resumeUrl = signupResumeUrl(user, token);
      if (resumeUrl) {
        window.location.replace(resumeUrl);
        return;
      }
      dispatch(setTokenAndUser({ token, user }));
      // Drop the outgoing session's route table before navigating. Invalidating
      // it (the old behaviour) kept serving the PREVIOUS tenant's tabs until the
      // refetch landed, and those paths embed the old tenant id — so the router
      // briefly held routes that can never resolve for this user.
      resetRoutesForNewSession();
      // `replace` so the browser Back button can't return to the previous
      // session's URL, which would 404 under the new identity. A link opened
      // while signed out lands on that link; signing in as someone else than
      // this tab's last user still goes home (useIdentityRouteGuard).
      navigate(takeReturnTo() ?? "/", { replace: true });
      toast.success("Welcome back");
    } catch (e) {
      toast.error("Sign in failed. Check your email and password.");
      console.log(e, "error");
    }
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row font-sans overflow-hidden">
      {/* --- Left: editorial green scene (desktop) — calm mesh + abstract line --- */}
      <aside className="relative hidden lg:flex lg:w-[44%] xl:w-[42%] flex-col min-h-screen shrink-0 overflow-hidden text-white">
        {/* Deep base + light from top-left (forest / editorial) */}
        <div
          className="absolute inset-0 z-0"
          style={{
            background: `
              radial-gradient(ellipse 90% 70% at 0% 0%, rgba(255,255,255,0.14) 0%, transparent 52%),
              radial-gradient(ellipse 80% 60% at 100% 100%, rgba(0,0,0,0.42) 0%, transparent 55%),
              linear-gradient(165deg, var(--color-primary) 0%, #0a1f18 42%, #030a08 100%)
            `,
          }}
        />
        {/* Soft color orbs — depth without “tech” UI */}
        <div
          className="absolute -top-24 -left-24 z-0 h-[22rem] w-[22rem] rounded-full opacity-[0.45] blur-3xl pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, var(--color-primary) 0%, transparent 68%)",
          }}
        />
        <div className="absolute -bottom-32 right-0 z-0 h-[28rem] w-[28rem] rounded-full bg-black/25 blur-3xl pointer-events-none" />
        {/* Subtle grain — stops flat solid fills */}
        <div
          className="absolute inset-0 z-[1] pointer-events-none opacity-[0.07] mix-blend-overlay"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          }}
        />
        {/* Abstract bottom curve — decorative only */}
        <svg
          className="absolute bottom-0 left-0 right-0 z-[1] h-[42%] min-h-[200px] w-full pointer-events-none text-white/[0.06]"
          viewBox="0 0 800 320"
          preserveAspectRatio="none"
          aria-hidden
        >
          <path
            fill="currentColor"
            d="M0,180 C180,80 380,220 520,140 C640,80 720,120 800,60 L800,320 L0,320 Z"
          />
          <path
            fill="currentColor"
            className="text-white/[0.04]"
            d="M0,220 C200,120 420,260 580,180 C680,130 740,150 800,100 L800,320 L0,320 Z"
          />
        </svg>

        <div className="relative z-10 flex flex-1 flex-col justify-center px-12 xl:px-16 py-14">
          <div className="mb-10">
            <img
              src={xaleLogo}
              alt=""
              className="h-9 w-auto object-contain opacity-95 drop-shadow-md"
            />
          </div>
          <p className="max-w-[20rem] text-[clamp(1.125rem,2.1vw,1.5rem)] font-light leading-[1.45] tracking-[-0.02em] text-white/95">
            Sign in to pick up leads, calls, and follow-ups in one place.
          </p>
          <p className="mt-8 max-w-xs text-[14px] leading-relaxed text-white/55">
            Your workspace for every conversation that matters.
          </p>
        </div>

        <div className="relative z-10 px-12 xl:px-16 pb-12 text-[12px] text-white/45 tracking-wide">
          © {new Date().getFullYear()} Xale
        </div>
      </aside>

      {/* --- Right: form (mobile = full width) --- */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-10 sm:py-14 lg:py-8 bg-[#f8f9fa] relative overflow-y-auto min-h-screen lg:min-h-0">
        {/* Mobile: slim green accent so the brand color shows on small screens too */}
        <div
          className="lg:hidden absolute top-0 left-0 right-0 h-1.5 z-0"
          style={{ background: "var(--color-primary-gradient)" }}
        />

        <div className="relative z-10 w-full max-w-[420px] flex flex-col items-center">
          {/* Logo on mobile only (desktop has left panel) */}
          <div className="mb-8 text-center lg:hidden">
            <div className="inline-flex items-center justify-center mb-4">
              <img
                src={xaleLogo}
                alt="Xale"
                className="h-10 w-auto object-contain"
              />
            </div>
            <h1 className="text-[1.5rem] font-semibold tracking-tight text-[var(--color-black-9)]">
              Sign in to Xale
            </h1>
            <p className="mt-2 text-[15px] leading-snug text-[var(--color-black-5)] max-w-sm mx-auto">
              Enter your work email and password to open your workspace.
            </p>
          </div>

          {/* Desktop: headline only (no duplicate huge logo) */}
          <div className="hidden lg:block mb-8 text-center w-full">
            <h1 className="text-[1.75rem] font-semibold tracking-tight text-[var(--color-black-9)]">
              Sign in to Xale
            </h1>
            <p className="mt-2 text-[15px] leading-snug text-[var(--color-black-5)]">
              Enter your work email and password to open your workspace.
            </p>
          </div>

          <div className="w-full rounded-2xl bg-[var(--color-white)] p-8 sm:p-9 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.08),0_0_0_1px_var(--color-black-2)]">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              <div className="space-y-1.5">
                <label
                  htmlFor="login-email"
                  className="block text-[13px] font-medium text-[var(--color-black-7)]"
                >
                  Email
                </label>
                <div className="relative group">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-[var(--color-black-4)] group-focus-within:text-[var(--color-primary)] transition-colors pointer-events-none" />
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    disabled={isLoading}
                    placeholder="you@company.com"
                    className="flex h-12 w-full rounded-xl border-2 border-transparent bg-[var(--color-green-1)] pl-11 pr-4 text-[15px] text-[var(--color-black-9)] placeholder:text-[var(--color-black-4)] focus:border-[var(--color-primary)] focus:bg-white focus:outline-none transition-all disabled:opacity-50"
                    {...register("email")}
                  />
                </div>
                {errors.email && (
                  <p className="text-[13px] text-[var(--color-high)]">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <label
                  htmlFor="login-password"
                  className="block text-[13px] font-medium text-[var(--color-black-7)]"
                >
                  Password
                </label>
                <div className="relative group">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-[var(--color-black-4)] group-focus-within:text-[var(--color-primary)] transition-colors pointer-events-none" />
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    disabled={isLoading}
                    placeholder="Your password"
                    className="flex h-12 w-full rounded-xl border-2 border-transparent bg-[var(--color-green-1)] pl-11 pr-12 text-[15px] text-[var(--color-black-9)] placeholder:text-[var(--color-black-4)] focus:border-[var(--color-primary)] focus:bg-white focus:outline-none transition-all disabled:opacity-50"
                    {...register("password")}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 p-1 rounded-md text-[var(--color-black-4)] hover:text-[var(--color-primary)] hover:bg-[var(--color-green-1)] transition-colors"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-[18px] w-[18px]" />
                    ) : (
                      <Eye className="h-[18px] w-[18px]" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-[13px] text-[var(--color-high)]">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex items-center justify-center gap-2 h-12 rounded-xl text-white text-[15px] font-semibold shadow-md hover:shadow-lg hover:brightness-[1.02] active:scale-[0.99] transition-all disabled:opacity-70 disabled:pointer-events-none mt-2"
                style={{ background: "var(--color-primary-gradient)" }}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    Sign in
                    <ArrowRight className="h-4 w-4 opacity-90" />
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="mt-10 w-full text-center">
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-[13px] text-[var(--color-black-5)]">
              <a
                href={`${MARKETING_BASE}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--color-primary)] transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href={`${MARKETING_BASE}/terms-of-service`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--color-primary)] transition-colors"
              >
                Terms of Service
              </a>
              <a
                href={`${MARKETING_BASE}/data-deletion`}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[var(--color-primary)] transition-colors"
              >
                Data deletion
              </a>
            </div>
            <div className="mt-5 flex items-center justify-center gap-2 text-[12px] text-[var(--color-black-4)] leading-relaxed">
              <img
                src={xaleLogo}
                alt=""
                className="h-4 w-4 rounded-full shrink-0"
              />
              <span>
                © {new Date().getFullYear()} Xale Private Limited. All rights
                reserved.
              </span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
